import React, { useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Upload, X, FileText, FileSpreadsheet, File } from 'lucide-react';
import { AdminTenantFormData } from './types';

interface Props {
  data: AdminTenantFormData;
  updateData: (updates: Partial<AdminTenantFormData>) => void;
}

const getFileIcon = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return <FileText className="h-4 w-4 text-red-500" />;
  if (['xls', 'xlsx', 'csv'].includes(ext || '')) return <FileSpreadsheet className="h-4 w-4 text-green-500" />;
  if (['doc', 'docx'].includes(ext || '')) return <FileText className="h-4 w-4 text-blue-500" />;
  return <File className="h-4 w-4 text-muted-foreground" />;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const StepDocuments: React.FC<Props> = ({ data, updateData }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      updateData({ documentFiles: [...data.documentFiles, ...Array.from(e.target.files)] });
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    const updated = data.documentFiles.filter((_, i) => i !== index);
    updateData({ documentFiles: updated });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      updateData({ documentFiles: [...data.documentFiles, ...Array.from(e.dataTransfer.files)] });
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>FAQs & Documents</Label>
        <p className="text-sm text-muted-foreground mt-1">
          Upload FAQs, service guides, pricing sheets, or any documents to train the assistant
        </p>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFilesChange}
        accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
        multiple
        className="hidden"
      />

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
        <p className="text-sm font-medium">
          Drag and drop files here, or click to browse
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Supports PDF, DOC, DOCX, TXT, CSV, XLS, XLSX
        </p>
      </div>

      {data.documentFiles.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">
            {data.documentFiles.length} file{data.documentFiles.length !== 1 ? 's' : ''} selected
          </Label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {data.documentFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30"
              >
                {getFileIcon(file.name)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-muted/50 rounded-lg p-4">
        <p className="text-sm text-muted-foreground">
          <strong>💡 Tip:</strong> The more relevant documents you provide, the better your 
          assistant will be at answering client queries accurately.
        </p>
      </div>
    </div>
  );
};

export default StepDocuments;
